import { createServerFn } from "@tanstack/react-start";
import { useSession } from "@tanstack/react-start/server";
import { createHash, timingSafeEqual } from "node:crypto";

type GateSession = { unlocked?: boolean };

function getSessionConfig() {
  return {
    password: process.env.CONTENT_SESSION_SECRET!,
    name: "uploads-gate",
    maxAge: 60 * 60 * 24 * 7,
    cookie: {
      httpOnly: true,
      secure: true,
      sameSite: "none" as const,
      path: "/",
    },
  };
}

function passwordMatches(input: string, expected: string): boolean {
  const a = createHash("sha256").update(input, "utf8").digest();
  const b = createHash("sha256").update(expected, "utf8").digest();
  return timingSafeEqual(a, b);
}

export const unlockUploads = createServerFn({ method: "POST" })
  .inputValidator((data: { password: string }) => data)
  .handler(async ({ data }) => {
    const expected = process.env.UPLOADS_PASSWORD;
    if (!expected) throw new Error("UPLOADS_PASSWORD is not set");
    if (!passwordMatches(data.password, expected)) {
      return { ok: false as const };
    }
    const session = await useSession<GateSession>(getSessionConfig());
    await session.update({ unlocked: true });
    return { ok: true as const };
  });

export const getUploadsUnlockStatus = createServerFn({ method: "GET" }).handler(
  async () => {
    const session = await useSession<GateSession>(getSessionConfig());
    return { unlocked: Boolean(session.data.unlocked) };
  },
);

type UploadRow = {
  id: string;
  source: "estimate" | "green_home_plans";
  submitted_at: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  postcode: string | null;
  source_page: string | null;
  traffic_source: string | null;
  notes: string | null;
  files: { label: string; url: string; filename: string }[];
  delivery?: "sent" | "failed" | null;
};

export const listUploads = createServerFn({ method: "GET" }).handler(async () => {
  const session = await useSession<GateSession>(getSessionConfig());
  if (!session.data.unlocked) {
    return { unlocked: false as const, rows: [] as UploadRow[] };
  }

  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const rows: UploadRow[] = [];

  async function sign(bucket: string, path: string | null | undefined) {
    if (!path) return null;
    const { data } = await supabaseAdmin.storage
      .from(bucket)
      .createSignedUrl(path, 60 * 60 * 24 * 30);
    return data?.signedUrl ?? null;
  }

  const { data: estimates } = await supabaseAdmin
    .from("estimate_details")
    .select(
      "id, created_at, contact_name, contact_email, contact_phone, postcode, source_page, traffic_source, annual_kwh, unit_rate_p, monthly_spend_gbp, electricity_bill_path, gas_bill_path, skipped",
    )
    .order("created_at", { ascending: false })
    .limit(100);

  for (const r of estimates ?? []) {
    const files: UploadRow["files"] = [];
    const elecUrl = await sign("customer-bills", r.electricity_bill_path);
    if (elecUrl && r.electricity_bill_path) {
      files.push({
        label: "Electricity bill",
        url: elecUrl,
        filename: r.electricity_bill_path.split("/").pop() ?? "electricity",
      });
    }
    const gasUrl = await sign("customer-bills", r.gas_bill_path);
    if (gasUrl && r.gas_bill_path) {
      files.push({
        label: "Gas bill",
        url: gasUrl,
        filename: r.gas_bill_path.split("/").pop() ?? "gas",
      });
    }
    const noteParts: string[] = [];
    if (r.annual_kwh != null) noteParts.push(`${r.annual_kwh} kWh/yr`);
    if (r.unit_rate_p != null) noteParts.push(`${r.unit_rate_p}p/kWh`);
    if (r.monthly_spend_gbp != null) noteParts.push(`£${r.monthly_spend_gbp}/mo`);
    if (r.skipped) noteParts.push("skipped upload");

    rows.push({
      id: `est-${r.id}`,
      source: "estimate",
      submitted_at: r.created_at,
      name: r.contact_name,
      email: r.contact_email,
      phone: r.contact_phone,
      postcode: r.postcode,
      source_page: r.source_page,
      traffic_source: r.traffic_source ?? null,
      notes: noteParts.join(" · ") || null,
      files,
    });
  }

  const { data: grants } = await supabaseAdmin
    .from("form_submissions")
    .select(
      "id, created_at, name, email, phone, postcode, source_page, traffic_source, service_of_interest, message, attachment_url",
    )
    .eq("form_type", "grants_assessment")
    .order("created_at", { ascending: false })
    .limit(100);

  for (const r of grants ?? []) {
    const files: UploadRow["files"] = [];
    // attachment_url stores a signed URL at time of upload; extract the storage
    // path and re-sign so links stay fresh.
    let storagePath: string | null = null;
    if (r.attachment_url) {
      const match = r.attachment_url.match(
        /\/object\/sign\/retrofit-assessments\/([^?]+)/,
      );
      if (match) storagePath = decodeURIComponent(match[1]);
    }
    const fresh = await sign("retrofit-assessments", storagePath);
    if (fresh && storagePath) {
      files.push({
        label: "Assessment file",
        url: fresh,
        filename: storagePath.split("/").pop() ?? "assessment",
      });
    } else if (r.attachment_url) {
      files.push({
        label: "Assessment file",
        url: r.attachment_url,
        filename: "assessment",
      });
    }

    rows.push({
      id: `gh-${r.id}`,
      source: "green_home_plans",
      submitted_at: r.created_at,
      name: r.name,
      email: r.email,
      phone: r.phone,
      postcode: r.postcode,
      source_page: r.source_page,
      traffic_source: r.traffic_source ?? null,
      notes:
        [r.service_of_interest, r.message].filter(Boolean).join(" · ") || null,
      files,
    });
  }

  rows.sort((a, b) => (a.submitted_at < b.submitted_at ? 1 : -1));

  const leadIds = rows.map((r) => r.id.replace(/^(est|gh)-/, ""));
  if (leadIds.length) {
    const { data: deliveries } = await supabaseAdmin
      .from("webhook_deliveries")
      .select("lead_id, status, created_at")
      .in("lead_id", leadIds)
      .order("created_at", { ascending: true });
    const latest = new Map<string, string>();
    for (const d of deliveries ?? []) if (d.lead_id) latest.set(d.lead_id, d.status);
    for (const r of rows) {
      const st = latest.get(r.id.replace(/^(est|gh)-/, ""));
      r.delivery = st === "sent" ? "sent" : st === "failed" ? "failed" : null;
    }
  }

  return { unlocked: true as const, rows };
});

/** Rebuilds the original webhook payload from the stored row and re-sends it. */
export const resendLeadWebhook = createServerFn({ method: "POST" })
  .inputValidator((data: { ids: string[]; password?: string }) => data)
  .handler(async ({ data }) => {
    const session = await useSession<GateSession>(getSessionConfig());
    const expected = process.env.UPLOADS_PASSWORD;
    const allowed =
      Boolean(session.data.unlocked) ||
      Boolean(expected && data.password && passwordMatches(data.password, expected));
    if (!allowed) return { ok: false as const, results: [] };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { deliverWebhook } = await import("@/lib/forms.server");
    const zap = process.env.ZAPIER_ENQUIRY_WEBHOOK_URL;
    const results: { id: string; ok: boolean; status: number | null; error: string | null }[] = [];

    for (const raw of data.ids.slice(0, 50)) {
      const isEst = raw.startsWith("est-");
      const id = raw.replace(/^(est|gh|fs)-/, "");
      if (isEst) {
        const { data: r } = await supabaseAdmin.from("estimate_details").select("*").eq("id", id).maybeSingle();
        if (!r) { results.push({ id: raw, ok: false, status: null, error: "not found" }); continue; }
        const sign = async (p: string | null) =>
          p ? (await supabaseAdmin.storage.from("customer-bills").createSignedUrl(p, 60 * 60 * 24 * 30)).data?.signedUrl ?? null : null;
        const res = await deliverWebhook(
          {
            subject: `New estimate details: ${r.contact_name ?? "Unknown"}${r.postcode ? ` (${r.postcode})` : ""}`,
            lead_id: r.id, submitted_at: r.created_at, form_type: "estimate_details",
            name: r.contact_name, email: r.contact_email, phone: r.contact_phone,
            address_line1: r.address_line1, address_line2: r.address_line2, town: r.town, postcode: r.postcode,
            source_page: r.source_page, annual_kwh: r.annual_kwh, unit_rate_p: r.unit_rate_p, monthly_spend_gbp: r.monthly_spend_gbp,
            electricity_bill_uploaded: Boolean(r.electricity_bill_path), electricity_bill_url: await sign(r.electricity_bill_path),
            electricity_bill_storage_path: r.electricity_bill_path,
            gas_bill_uploaded: Boolean(r.gas_bill_path), gas_bill_url: await sign(r.gas_bill_path), gas_bill_storage_path: r.gas_bill_path,
            uploads_dashboard_url: "https://crsenergy.org/uploads", skipped: r.skipped,
            source: r.traffic_source, source_medium: r.traffic_medium, source_campaign: r.traffic_campaign,
            source_referrer: r.traffic_referrer, landing_page: r.landing_page, resent: true,
          },
          { url: process.env.GHL_ESTIMATE_DETAILS_WEBHOOK_URL ?? zap, webhook: "zapier_estimate_details", leadTable: "estimate_details", leadId: r.id },
        );
        results.push({ id: raw, ...res });
      } else {
        const { data: r } = await supabaseAdmin.from("form_submissions").select("*").eq("id", id).maybeSingle();
        if (!r) { results.push({ id: raw, ok: false, status: null, error: "not found" }); continue; }
        const res = await deliverWebhook(
          {
            subject: `New ${r.form_type} enquiry: ${r.name ?? "Unknown"}${r.postcode ? ` (${r.postcode})` : ""}`,
            lead_id: r.id, submitted_at: r.created_at, form_type: r.form_type,
            name: r.name, phone: r.phone, email: r.email, postcode: r.postcode,
            service_of_interest: r.service_of_interest, message: r.message, source_page: r.source_page,
            gclid: r.gclid, user_agent: r.user_agent, attachment_url: r.attachment_url,
            source: r.traffic_source, source_medium: r.traffic_medium, source_campaign: r.traffic_campaign,
            source_referrer: r.traffic_referrer, landing_page: r.landing_page, resent: true,
          },
          { url: zap, webhook: "zapier_enquiry", leadTable: "form_submissions", leadId: r.id },
        );
        results.push({ id: raw, ...res });
      }
    }
    return { ok: true as const, results };
  });
